/**
 * Branded Types - Type-safe ID wrappers using Zod 4.1.9
 *
 * Zod 4 API: z.string().brand("BrandName") - takes string argument
 */
import { z } from 'zod';

// ============================================================================
// Brand Type Helper (Zod 4 API)
// ============================================================================

/**
 * Creates a branded UUID type.
 * Zod 4: brand() takes a string argument, not a generic type parameter.
 */
export const createBrandedUuid = <Brand extends string>(brand: Brand) =>
  z.string().uuid().brand(brand);

/**
 * Creates a branded string type (non-UUID).
 */
export const createBrandedString = <Brand extends string>(brand: Brand) =>
  z.string().brand(brand);

/**
 * Creates a branded UUID type without RFC 4122 variant enforcement.
 * Cognito user sub values can fail strict UUID validation.
 */
const UUID_LAX_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const createBrandedUuidLoose = <Brand extends string>(brand: Brand) =>
  z.string().regex(UUID_LAX_REGEX, 'Invalid UUID').brand(brand);

// ============================================================================
// Core IDs (UUIDs)
// ============================================================================

export const OrgId = createBrandedUuid('OrgId');
export type OrgId = z.infer<typeof OrgId>;

export const UserId = createBrandedUuidLoose('UserId');
export type UserId = z.infer<typeof UserId>;

export const ApplicationId = createBrandedUuid('ApplicationId');
export type ApplicationId = z.infer<typeof ApplicationId>;

// ============================================================================
// Config and Course IDs (Strings)
// ============================================================================

export const ModuleKey = createBrandedString('ModuleKey');
export type ModuleKey = z.infer<typeof ModuleKey>;

export const CourseId = createBrandedString('CourseId');
export type CourseId = z.infer<typeof CourseId>;

export const TermId = createBrandedString('TermId');
export type TermId = z.infer<typeof TermId>;

export const ResourceId = createBrandedString('ResourceId');
export type ResourceId = z.infer<typeof ResourceId>;

// ============================================================================
// Academic Year (Branded)
// ============================================================================

/**
 * Academic year format: YYYY-YYYY
 * Example: 2025-2026
 */
export const AcademicYear = z.string()
  .regex(/^\d{4}-\d{4}$/, 'Academic year must be in format YYYY-YYYY')
  .brand('AcademicYear');
export type AcademicYear = z.infer<typeof AcademicYear>;

// ============================================================================
// Timestamp Type (Unix seconds, not milliseconds!)
// Per GRPO-Amplify-Critical.md: GraphQL Int is 32-bit, milliseconds overflow
// ============================================================================

/**
 * Unix timestamp in seconds (not milliseconds).
 * Max value: 2,147,483,647 (year 2038)
 * Current Unix timestamp: ~1,735,000,000 (2025)
 */
export const UnixTimestamp = z.number().int().nonnegative()
  .brand('UnixTimestamp');
export type UnixTimestamp = z.infer<typeof UnixTimestamp>;

export const UnixTs = z.number().int().nonnegative()
  .brand('UnixTs');
export type UnixTs = z.infer<typeof UnixTs>;

export const ContractHash = z.string()
  .regex(/^[a-f0-9]{64}$/, 'SHA-256 hash')
  .brand('ContractHash');
export type ContractHash = z.infer<typeof ContractHash>;

export const ScanId = createBrandedUuid('ScanId');
export type ScanId = z.infer<typeof ScanId>;

// ============================================================================
// Helpers
// ============================================================================

/** Convert Date to Unix timestamp (seconds). */
export const dateToUnix = (date: Date): number => Math.floor(date.getTime() / 1000);

/** Convert Unix timestamp (seconds) to Date. */
export const unixToDate = (unix: number): Date => new Date(unix * 1000);

/** Get current Unix timestamp (seconds). */
export const nowUnix = (): number => Math.floor(Date.now() / 1000);
